import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import type { BroadcastSeverity } from './use-platform-admin';
import { useDeferredReady } from './use-deferred-ready';

export interface ActiveBanner {
  id: string;
  severity: BroadcastSeverity;
  title: string;
  body: string;
  ctaLabel: string | null;
  ctaUrl: string | null;
}

/**
 * Fetches the banners that should be shown to the current user inside the
 * org app. Refetches every 5 minutes + on window focus so a banner authored
 * in /admin/banners shows up without a hard refresh.
 */
export function useActiveBanners() {
  // #1429: non-critical shell data — defer off the cold-load critical path.
  // A banner appearing ~1s after first paint is acceptable; freeing the
  // connection for the critical bootstrap is the win.
  const deferredReady = useDeferredReady();
  return useQuery({
    queryKey: ['system-banners', 'active'],
    queryFn: async () => {
      const res = await api.get<{ data: ActiveBanner[] }>(
        '/system/banners/active',
      );
      return res.data.data;
    },
    enabled: deferredReady,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useDismissBanner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/system/banners/${id}/dismiss`);
    },
    // Optimistic: hide immediately. If the request fails the next refetch
    // will bring it back.
    onMutate: async (id: string) => {
      await qc.cancelQueries({ queryKey: ['system-banners', 'active'] });
      const prev = qc.getQueryData<ActiveBanner[]>(['system-banners', 'active']);
      qc.setQueryData<ActiveBanner[]>(
        ['system-banners', 'active'],
        (old) => old?.filter((b) => b.id !== id) ?? [],
      );
      return { prev };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prev) {
        qc.setQueryData(['system-banners', 'active'], ctx.prev);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['system-banners', 'active'] });
    },
  });
}
